/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Sheets draws one embedded PNG at about 2000px. Tiles stay under that, matching KtsWorkbookHelper. */
export const GANTT_TILE_EDGE = 1600;

export interface GanttTileOrigin {
    x: number;
    y: number;
    width: number;
    height: number;
}

export function ganttExportPixels(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }
    return Math.round(value);
}

export function ganttTileOrigins(width: number, height: number, edge = GANTT_TILE_EDGE): GanttTileOrigin[] {
    const pixelWidth = ganttExportPixels(width);
    const pixelHeight = ganttExportPixels(height);
    const tiles: GanttTileOrigin[] = [];
    if (pixelWidth <= 0 || pixelHeight <= 0) {
        return tiles;
    }
    for (let y = 0; y < pixelHeight; y += edge) {
        for (let x = 0; x < pixelWidth; x += edge) {
            tiles.push({
                x,
                y,
                width: Math.min(edge, pixelWidth - x),
                height: Math.min(edge, pixelHeight - y)
            });
        }
    }
    return tiles;
}

export function ganttTileColumnCount(width: number, edge = GANTT_TILE_EDGE): number {
    const pixelWidth = ganttExportPixels(width);
    if (pixelWidth <= 0) {
        return 0;
    }
    return Math.ceil(pixelWidth / edge);
}

/** Same stack as styles.less. A serialized SVG image does not inherit it from the page. */
export const GANTT_EXPORT_FONT_FAMILY = 'Roboto, "Helvetica Neue", sans-serif';

/** Latin Roboto 400 from esci-tools/src/assets/css/googlefonts.css. */
export const ROBOTO_LATIN_400_URL = 'https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxK.woff2';

let cachedExportFont: Promise<string | null> | null = null;

export function svgWithExportFont(svg: SVGElement, fontDataUrl: string | null): string {
    const clone = svg.cloneNode(true) as SVGElement;
    if (!clone.getAttribute('xmlns')) {
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }
    clone.querySelectorAll('text').forEach(text => {
        text.setAttribute('font-family', GANTT_EXPORT_FONT_FAMILY);
        text.setAttribute('font-weight', '400');
    });
    if (fontDataUrl) {
        const style = clone.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent =
            `@font-face{font-family:'Roboto';font-style:normal;font-weight:400;src:url(${fontDataUrl}) format('woff2');}`;
        clone.insertBefore(style, clone.firstChild);
    }
    return new XMLSerializer().serializeToString(clone);
}

export function loadGanttExportFont(): Promise<string | null> {
    if (cachedExportFont === null) {
        cachedExportFont = fetch(ROBOTO_LATIN_400_URL)
            .then(response => response.ok ? response.arrayBuffer() : Promise.reject(new Error('Roboto was not loaded')))
            .then(buffer => `data:font/woff2;base64,${bytesToBase64(new Uint8Array(buffer))}`)
            .catch(() => {
                cachedExportFont = null;
                return null;
            });
    }
    return cachedExportFont;
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}
